import { type Dayjs } from 'dayjs';
import { useGoodsOptionsBrief } from '../queries/useGoodsOptionsBrief';
import { useCallback } from 'react';
import { useConfigs } from 'shared/src/queries';
import { getCurrentTimeDiff, getHour, isNil } from 'shared/src/utils';

const getThirdPLOptionDeadlineText = (limitTime: Dayjs): string => {
  // 23:59:59는 자정으로 취급 → 평일·토 자정 컷오프이므로 "컷오프 후" 상태가 발생하지 않음
  const isMidnight = limitTime.format('HH:mm:ss') === '23:59:59';

  if (isMidnight) {
    return '밤 12시 전 주문 시 내일 도착';
  }

  // 컷오프 이후
  const isTimeOver = getCurrentTimeDiff(limitTime) > 0;
  if (isTimeOver) {
    return '주문 마감으로 모레 도착';
  }

  // 컷오프 이전
  const timeText = limitTime.locale('ko').format('A h');
  return `${timeText}시 전 주문 시 내일 도착`;
};

export const useGetOrderDeadlineText = () => {
  const { data: optionsBrief } = useGoodsOptionsBrief();
  const { data: configs } = useConfigs();

  const getLimitTime = useCallback(
    (params: { delivery_type: 'shak' | 'today' | 'third_pl' }) => {
      if (isNil(optionsBrief)) return null;
      // configs?.shak_delivery.operation_end_time
      if (params.delivery_type === 'shak') {
        return getHour(configs?.shak_delivery.operation_end_time);
      }

      if (params.delivery_type === 'third_pl') {
        if (isNil(optionsBrief.third_pl_delivery_order_deadline_time)) {
          return null;
        }
        return getHour(optionsBrief.third_pl_delivery_order_deadline_time);
      }

      return getHour(optionsBrief.today_delivery_order_deadline_time);
    },
    [configs?.shak_delivery.operation_end_time, optionsBrief],
  );

  return useCallback<
    (params: {
      delivery_type: 'shak' | 'today' | 'third_pl' | 'standard';
    }) => string
  >(
    params => {
      if (isNil(optionsBrief)) {
        return '';
      }

      if (params.delivery_type === 'standard') {
        return '';
      }

      if (params.delivery_type === 'third_pl') {
        const limitTime = getLimitTime({ delivery_type: 'third_pl' });
        if (isNil(limitTime)) return '';

        return getThirdPLOptionDeadlineText(limitTime);
      }

      const limitTime = getLimitTime({ delivery_type: params.delivery_type });
      if (isNil(limitTime)) return '';

      const diffSecond = getCurrentTimeDiff(limitTime);
      const isTimeOver = diffSecond > 0;

      if (isTimeOver) {
        return '주문 마감으로 내일 출발';
      }

      const hour = limitTime.hour();
      return `${hour < 12 ? '오전 ' : '오후 '}${hour > 12 ? hour - 12 : hour}시 전 주문하면 오늘 출발`;
    },
    [getLimitTime, optionsBrief],
  );
};
